import * as React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Landing, MarketingHeader, MarketingFooter, type Lang } from "./marketing/Landing.js";
import { ComparisonHub, ComparisonPage, getCompetitor } from "./marketing/Comparison.js";
import { LegalPage } from "./marketing/Legal.js";
import { Login } from "./screens/Login.js";
import { Presenter } from "./screens/Presenter.js";
import { Participant } from "./screens/Participant.js";
import { Projection } from "./screens/Projection.js";
import { ResultsRoute } from "./screens/Results.js";
import { SeriesRoute } from "./screens/Series.js";
import { Upgrade } from "./screens/Upgrade.js";
import { Library } from "./screens/Library.js";
import { MethodDetail } from "./screens/MethodDetail.js";
import { Planner } from "./screens/Planner.js";
import { SharedAgendaView } from "./screens/SharedAgendaView.js";
import { getLocale, setLocale, type Locale } from "./i18n.js";
import { api } from "./api.js";

/** Public marketing chrome (header + footer + language state) wrapping any page. */
function MarketingShell({ children }: { children: (lang: Lang) => React.ReactNode }): React.ReactElement {
  const [lang, setLang] = React.useState<Lang>(getLocale());
  const onLangChange = (l: Lang): void => {
    setLocale(l as Locale);
    setLang(l);
  };
  return (
    <>
      <MarketingHeader lang={lang} onLangChange={onLangChange} />
      {children(lang)}
      <MarketingFooter />
    </>
  );
}

/** /comparar/:slug — resolve the competitor or bounce to the hub. */
function ComparisonRoute(): React.ReactElement {
  const { slug } = useParams();
  const competitor = getCompetitor(slug ?? "");
  if (!competitor) return <Navigate to="/comparar" replace />;
  return <MarketingShell>{(lang) => <ComparisonPage competitor={competitor} lang={lang} />}</MarketingShell>;
}

/** Gate host-only routes on a live session; otherwise bounce to /entrar. */
function RequireHost({ children }: { children: React.ReactElement }): React.ReactElement {
  const [state, setState] = React.useState<"checking" | "ok" | "no">("checking");
  React.useEffect(() => {
    let alive = true;
    void api.me().then((me) => {
      if (alive) setState(me.authenticated ? "ok" : "no");
    });
    return () => {
      alive = false;
    };
  }, []);
  if (state === "checking") {
    return <div className="p-10 text-center text-muted">…</div>;
  }
  if (state === "no") return <Navigate to="/entrar" replace />;
  return children;
}

function ParticipantRoute(): React.ReactElement {
  const { code } = useParams();
  return <Participant code={(code ?? "").toUpperCase()} />;
}
function ProjectionRoute(): React.ReactElement {
  const { id } = useParams();
  return <Projection sessionId={id ?? ""} />;
}
function SharedAgendaRoute(): React.ReactElement {
  const { token } = useParams();
  return <SharedAgendaView token={token ?? ""} />;
}

function MethodDetailRoute(): React.ReactElement {
  const { slug } = useParams();
  return <MethodDetail slug={decodeURIComponent(slug ?? "")} />;
}

export function App(): React.ReactElement {
  return (
    <Routes>
      <Route path="/" element={<MarketingShell>{(lang) => <Landing lang={lang} />}</MarketingShell>} />
      <Route path="/comparar" element={<MarketingShell>{(lang) => <ComparisonHub lang={lang} />}</MarketingShell>} />
      <Route path="/comparar/:slug" element={<ComparisonRoute />} />
      <Route path="/privacidade" element={<MarketingShell>{(lang) => <LegalPage kind="privacy" lang={lang} />}</MarketingShell>} />
      <Route path="/termos" element={<MarketingShell>{(lang) => <LegalPage kind="terms" lang={lang} />}</MarketingShell>} />
      <Route path="/entrar" element={<Login />} />
      <Route path="/host" element={<RequireHost><Presenter /></RequireHost>} />
      <Route path="/projecao/:id" element={<ProjectionRoute />} />
      <Route path="/resultados/:id" element={<RequireHost><ResultsRoute /></RequireHost>} />
      <Route path="/host/serie/:seriesId" element={<RequireHost><SeriesRoute /></RequireHost>} />
      {/* Where every 402 points. Host-only: the prices are public, but the CURRENT plan is not. */}
      <Route path="/planos" element={<RequireHost><Upgrade /></RequireHost>} />
      <Route path="/j/:code" element={<ParticipantRoute />} />
      <Route path="/kit" element={<Library />} />
      <Route path="/kit/m/:slug" element={<MethodDetailRoute />} />
      <Route path="/kit/planner" element={<Planner />} />
      <Route path="/kit/a/:token" element={<SharedAgendaRoute />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
